<template>
  <div class="time-sheet-details">
    <div class="header">
      <div>Time Sheet</div>
      <div>Reference# {{ timeSheet.referenceNumber }}</div>
    </div>
    <div class="logo-status-header">
      <div class="tes-logo">
        <img width="70%" height="70%" src="../../../../../images/tes-new-pdf-logo.jpg" />
      </div>
      <div><Status :status="timeSheet.status" object-type="time-sheet" /></div>
    </div>
    <div class="time-sheet-header">
      <div class="header-left-content">
        <div class="header-item">
          <div class="header-label">End Client</div>
          <div class="header-value">{{ endClientName }}</div>
        </div>
        <div class="header-item">
          <div class="header-label">Pay Period</div>
          <div class="header-value">
            <span class="nowrap">{{ startDate | formatDate }}</span>
            to
            <span class="nowrap">{{ endDate | formatDate }}</span>
          </div>
        </div>
        <div class="header-item">
          <div class="header-label">Contractor</div>
          <div class="header-value bolded">
            {{ timeSheet.assignment.contractor_name }}(#{{ timeSheet.assignment.payroll_number }})
          </div>
        </div>
        <div class="header-item">
          <div class="header-label">Client</div>
          <div class="header-value bolded">{{ timeSheet.assignment.client_name }}</div>
        </div>
      </div>
      <div class="header-right-content">
        <div class="header-label bolded">Pay Rates Legend:</div>
        <div v-for="item of timeSheet.assignment.rates" :key="item.id">
          <div class="header-item">
            <div class="header-label">{{ item.rate_code }}</div>
            <div class="header-value">
              {{
                rateTypes.filter(rate => rate.code == item.rate_code)[0]
                  ? rateTypes.filter(rate => rate.code == item.rate_code)[0].description
                  : ''
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <table class="time-sheet-details-body-table">
        <thead>
          <tr>
            <th>Date (yyyy-mm-dd)</th>
            <th v-for="rate in sortedRates" :key="rate.id">{{ rate.rateCode }}</th>
            <th>Total</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(entriesForDay, date, index) in entriesByDay"
            :key="date"
            :class="index % 2 == 0 ? 'odd' : 'even'"
          >
            <td class="date">{{ date | formatDateIgnoreTime('YYYY-MM-DD ddd') }}</td>
            <td v-for="entry in entriesForDay" :key="entry.id">
              {{ entry.quantity }}
            </td>
            <td class="hours">
              {{ dateTotalsHourly[date] }} hours
              <br v-if="dateTotalsDaily[date]" />
              <small v-if="dateTotalsDaily[date]">{{ dateTotalsDaily[date] }} dailies</small>
            </td>
            <td>
              <span v-if="notes[date]">{{ notes[date].note }}</span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th>Totals</th>

            <td v-for="(total, rate) in rateTotals" :key="rate">{{ total }}</td>

            <th>
              {{ grandTotalHours }} hours
              <br />
              <small v-if="grandTotalDays">{{ grandTotalDays }} dailies</small>
            </th>

            <th>
              <span v-if="notes['null']">{{ notes['null'].note }}</span>
            </th>
          </tr>
        </tfoot>
      </table>
    </div>
    <div class="time-sheet-attachments">
      <div class="time-sheet-attachments-header">
        Invoice/ Documents Attached
      </div>
      <div>
        <ul>
          <li v-for="(attachment, index) in attachments" :key="index">
            {{ attachment.category }} {{ attachment.originalName }} was updated by
            {{ attachment.last_name }}, {{ attachment.first_name }} on
            {{ attachment.createdAt | formatDateTime }}
          </li>
        </ul>
      </div>
    </div>
    <div class="time-sheet-history">
      <div class="time-sheet-history-header">Document History</div>
      <div>
        <table class="time-sheet-history-table">
          <thead>
            <tr>
              <th>Date (yyyy-mm-dd)</th>
              <th>Status</th>
              <th>User</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(historyRecord, index) in timeSheetHistories"
              :key="index"
              :class="index % 2 == 0 ? 'even' : 'odd'"
            >
              <td class="date">{{ historyRecord.createdAt | formatDateTime }}</td>
              <td>{{ historyRecord.status | readable | capitalize }}</td>
              <td>{{ historyRecord.createdBy | nameFromUserObject }}</td>
              <td>{{ historyRecord.approverComment }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import * as R from 'ramda';
import { AppGetters } from '@/app/app.keys';
import Status from '@/app/components/Status';
export const hourlyFilter = R.filter(n => n.unit === 'HOURLY');
export const dailyFilter = R.filter(n => n.unit === 'DAILY');

export const groupByDay = R.groupBy(entry => entry.entryDate);
export const groupByRate = R.groupBy(entry => `${entry.sortOrder}_${entry.timeSheetRateId}`);

// make an object with key=date and value=note object
export const createNotesObject = timeSheetNotes => {
  if (timeSheetNotes === undefined || timeSheetNotes === null) {
    return {};
  }
  return R.zipObj(R.map(R.prop('noteDate'), timeSheetNotes), R.clone(timeSheetNotes));
};

// notesObject is an object with many pairs of (key=noteDate, value=note object),
// we need to turn it into an array of notes
export const createNotesArray = notesObject => {
  const notesArray = [];
  for (const noteDate in notesObject) {
    notesArray.push(notesObject[noteDate]);
  }
  return notesArray;
};

export const sumFilteredQuantities = (filter, entries) => {
  return R.map(entry => {
    return R.sum(
      R.pipe(
        filter,
        R.pluck('quantity')
      )(entry)
    );
  })(entries);
};

export const sumQuantities = R.map(entry => {
  return R.sum(R.pluck('quantity')(entry));
});
export default {
  name: 'TimesheetDetailsPdf',
  components: { Status },
  props: {
    timeSheet: {
      type: Object,
      required: true
    },
    timeSheetAttachments: {
      type: Array,
      default: null
    },
    history: {
      type: Array,
      default: null
    },
    batch: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      entries: [],
      entriesByDay: {},
      entriesByRate: {},
      notes: {}
    };
  },
  computed: {
    timeSheetHistories() {
      if (this.batch) {
        return this.history;
      }
      return this.$store.state.timeSheetsState.timeSheetDetailsState.timeSheetHistories;
    },
    statusColor() {
      switch (this.timeSheet.status) {
        case 'PROCESSED':
          return 'processed';
        case 'APPROVED':
          return 'approved';
        case 'SUBMITTED':
          return 'submitted';
      }
      return 'draft';
    },
    // startDate and endDate computed functions are only required right now because
    // the api does not return these values. Once the api has been changed to return
    // all the fields from the time_sheets_view, we won't need these two functions.
    startDate() {
      return this.timeSheet.timeSheetEntries[0].entryDate;
    },
    endDate() {
      return this.timeSheet.timeSheetEntries[this.timeSheet.timeSheetEntries.length - 1].entryDate;
    },
    sortedRates() {
      // We always want to display the columns in the order: regular, overtime, pager shifts
      if (this.timeSheet && this.timeSheet.timeSheetRates) {
        return R.sortBy(R.prop('sortOrder'))(this.timeSheet.timeSheetRates);
      }
      return [];
    },
    dateTotalsHourly() {
      return sumFilteredQuantities(hourlyFilter, this.entriesByDay);
    },
    dateTotalsDaily() {
      return sumFilteredQuantities(dailyFilter, this.entriesByDay);
    },
    rateTotals() {
      return sumQuantities(this.entriesByRate);
    },
    grandTotalHours() {
      return R.sum(R.values(this.dateTotalsHourly));
    },
    grandTotalDays() {
      return R.sum(R.values(this.dateTotalsDaily));
    },
    rateTypes() {
      return this.$store.getters[AppGetters.AssignmentRateTypes];
    },
    attachments() {
      if (this.batch) {
        this.timeSheetAttachments;
      }
      return this.timeSheet.attachments
        ? this.timeSheet.attachments
        : this.$store.state.timeSheetsState.timeSheetDetailsState.attachments;
    },
    endClientName() {
      return this.timeSheet.assignment && this.timeSheet.assignment.end_client_name
        ? this.timeSheet.assignment.end_client_name
        : '';
    }
  },
  watch: {
    timeSheet: {
      immediate: true,
      handler() {
        this.entries = R.clone(this.timeSheet.timeSheetEntries);
        if (this.entries !== undefined) {
          this.entriesByDay = groupByDay(this.entries);
          for (const item of Object.keys(this.entriesByDay)) {
            this.entriesByDay[item] = this.sortByRateCode(this.entriesByDay[item]);
          }
          this.entriesByRate = groupByRate(this.entries);
          this.notes = createNotesObject(this.timeSheet.timeSheetNotes);
        }
      }
    }
  },
  methods: {
    sortByRateCode(entry) {
      return entry.sort((a, b) => {
        return a.sortOrder - b.sortOrder;
      });
    }
  }
};
</script>
<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
.time-sheet-details {
  margin: 1% 5%;
  font-size: 1rem;
  .header {
    display: flex;
    flex-direction: row;
    color: $grey;
    padding-top: 0px;
    padding-bottom: 5px;
    justify-content: space-between;
    font-size: 0.6rem;
  }

  .logo-status-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .time-sheet-header {
    display: flex;
    flex-direction: row;
    break-inside: avoid;
    width: 100%;

    .header-left-content {
      width: 50%;
    }
    .header-item {
      display: flex;
      flex-direction: row;
      break-inside: avoid;

      .header-label {
        font-weight: bold;
      }
      .header-value {
        padding-left: 10%;
      }
    }
  }

  .time-sheet-details-body-table,
  .time-sheet-history-table {
    font-size: 0.9rem;
    margin-top: 3%;
    thead {
      border-bottom: 0.5pt solid $black;
      font-weight: bold;
      page-break-inside: avoid;
      padding-bottom: 1px;
    }
    tbody {
      tr {
        border-bottom: 0.25pt solid $row-border;
        padding-bottom: 0%;
        margin-bottom: 0%;
      }
    }

    .even {
      background-color: $background-level-3;
    }
  }

  .bolded {
    font-weight: bold;
  }
}
.time-sheet-history,
.time-sheet-attachments {
  margin-top: 3%;
  .time-sheet-history-header,
  .time-sheet-attachments-header {
    font-size: 1rem;
    font-weight: bold;
  }
}
</style>
